import { IQuestion } from 'types/Question.types';
import {
  CheckboxWrapper,
  Column,
  ContainerGrayBottomBorder,
  FormItemContainer,
  QuestionName,
  QuestionNameAsteriskInfoRow,
  RedAsterisk,
  RedAsteriskWrapper,
  TooltipWrapper,
} from './QuestionRow.styled';
import Tooltip from 'components/Tooltip/Tooltip';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import { Info } from '@phosphor-icons/react';
import { capitalizeFirstLetter } from 'utils/stringUtils';
import FormItem from '../FormItem/FormItem';
import { FormikContextType, useFormikContext } from 'formik';
import { useClearFormItemValue } from './hooks';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { useTranslation } from 'react-i18next';
import { useIsQuotationMode } from 'utils/hooks/useIsQuotationMode';
import { useQuotationLineResolveParams } from 'utils/hooks/useQuotationLineResolveParams';
import { Checkbox } from 'components/Checkbox/Checkbox';
import {
  inlineControlStacksWhenNarrow,
  isInlineControlQuestion,
  questionCanBeCleared,
} from '../FormItem/helpers';
import { setIndicativeQuestionKeys } from 'store/SubmitConfiguration/actions/submitConfiguration';

interface IQuestionRowProps {
  question: IQuestion;
  hasValue: boolean;
  hasImages: boolean;
  showBorder: boolean;
  questionPrice: number | undefined;
}

export const QuestionRow = ({
  question,
  hasValue,
  hasImages,
  showBorder,
  questionPrice,
}: IQuestionRowProps) => {
  const clearFormItemValue = useClearFormItemValue();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isQuotationMode = useIsQuotationMode();
  const isResolvingQuotationLine = !!useQuotationLineResolveParams();

  const context: FormikContextType<any> = useFormikContext();

  const isDisabled = !!question.disabled;

  // A required question with nothing else to pick cannot be emptied: the
  // backend answers it again on the next query once one valid option is left,
  // so clearing would only make the value flicker and come back. This is the
  // backend's own condition, which does not look at how the answer came about -
  // an answer the user chose before the options narrowed is filled back in just
  // the same. Options are already filtered to the valid ones.
  const isOnlyAnswerLeft = question.required && question.options.length === 1;

  // Booleans are never cleared: both states are answers.
  const canClear = questionCanBeCleared(question.type);
  const showClear = canClear && hasValue && !isDisabled && !isOnlyAnswerLeft;
  const clearValue = () =>
    clearFormItemValue(context.setFieldValue, question, question.initialKey);

  // Fallback to [] because persisted state rehydrated from before this field
  // existed will not have it (redux-persist).
  const indicativeQuestionKeys = useSelector(
    (state: IRootReducerState) =>
      state.submitConfigurationInfo.indicativeQuestionKeys ?? []
  );
  const isIndicative = indicativeQuestionKeys.includes(question.initialKey);

  // Interim: show on every visible question while in quotation mode. Never in
  // resolve mode, where the point is to make indicative answers final.
  const canMarkIndicative = isQuotationMode && !isResolvingQuotationLine;

  // Highlight the answers the user came here to resolve.
  const needsResolving = isResolvingQuotationLine && isIndicative;

  const toggleIndicative = () => {
    dispatch(
      setIndicativeQuestionKeys(
        isIndicative
          ? indicativeQuestionKeys.filter(
              (key: string) => key !== question.initialKey
            )
          : [...indicativeQuestionKeys, question.initialKey]
      )
    );
  };

  return question.hidden ? null : (
    <ContainerGrayBottomBorder showBorder={showBorder} disabled={isDisabled}>
      <FormItemContainer
        hasValue={hasValue}
        disabled={isDisabled}
        needsResolving={needsResolving}
        data-needs-resolving={needsResolving || undefined}
        isInlineControl={isInlineControlQuestion(question.type)}
        stacksWhenNarrow={inlineControlStacksWhenNarrow(question.type)}
      >
        <Column>
          <QuestionNameAsteriskInfoRow>
            <QuestionName>
              {capitalizeFirstLetter(question.name).replace('_', ' ')}
            </QuestionName>
            {question.required && !hasValue && (
              <RedAsteriskWrapper>
                <RedAsterisk>*</RedAsterisk>
              </RedAsteriskWrapper>
            )}
            {question.description && (
              <TooltipWrapper>
                <Tooltip content={question.description}>
                  {question.description ? (
                    <Icon svg={Info} size={20} color={COLORS.GREEN} />
                  ) : null}
                </Tooltip>
              </TooltipWrapper>
            )}
          </QuestionNameAsteriskInfoRow>
        </Column>
        <FormItem
          question={question}
          hasImages={hasImages}
          questionPrice={questionPrice}
          showClear={showClear}
          reserveClear={canClear}
          allowClear={!isOnlyAnswerLeft}
          onClear={clearValue}
        />
        {canMarkIndicative && (
          <CheckboxWrapper>
            <Checkbox
              label={t('Indicative')}
              isChecked={isIndicative}
              onChange={toggleIndicative}
              isDisabled={isDisabled}
            />
          </CheckboxWrapper>
        )}
      </FormItemContainer>
    </ContainerGrayBottomBorder>
  );
};
